<template>
  <div v-click-outside="hideTimestamp" class="message-container">
    <FriedParagraph v-if="showName" :size="ParagraphSize.Small" class="recipient-name">
      {{ name }}
    </FriedParagraph>
    <div class="message-content">
      <FriedAvatar
        :size="AvatarSize.Small"
        :class="['recipient-avatar', { show: last }]"
        :name="name"
        :thumb-url="thumbUrl"
      />
      <div
        :class="['text', { active: showMessageTimestamp, first, last }]"
        :data-external="`name:${name}-message-comment:${comment.commentText}-text`"
        @click="showMessageTimestamp = true"
      >
        <FriedParagraph>{{ comment.commentText }} </FriedParagraph>
      </div>
      <FriedIconButton
        v-if="showDelete"
        ref="delete"
        v-wave
        class="delete-button"
        :data-external="`name:${name}-message-comment:${comment.commentText}-delete-text-button`"
        @click="$emit('delete', comment.id)"
      >
        <FriedIcon icon="delete-bin" />
      </FriedIconButton>
    </div>
    <TransitionExpand>
      <FriedParagraph
        v-if="showMessageTimestamp"
        :size="ParagraphSize.Small"
        class="message-timestamp"
      >
        {{ fullTimestamp }}
      </FriedParagraph>
    </TransitionExpand>
  </div>
</template>

<script lang="ts">
import { ParagraphSize, AvatarSize } from '@getaccept/fried-tofu';
import type { PropType } from 'vue';
import { defineComponent, ref } from 'vue';
import type { ChatComment } from '../types/chat-comment';

export default defineComponent({
  props: {
    comment: {
      type: Object as PropType<ChatComment>,
      default: () => ({}),
    },
    name: {
      type: String,
      default: '',
    },
    thumbUrl: {
      type: String,
      default: '',
    },
    fullTimestamp: {
      type: String,
      default: '',
    },
    showName: {
      type: Boolean,
    },
    first: {
      type: Boolean,
    },
    last: {
      type: Boolean,
    },
    showDelete: {
      type: Boolean,
    },
  },
  emits: ['delete'],
  setup() {
    const showMessageTimestamp = ref(false);

    const hideTimestamp = () => {
      showMessageTimestamp.value = false;
    };

    return {
      ParagraphSize,
      AvatarSize,
      showMessageTimestamp,
      hideTimestamp,
    };
  },
});
</script>

<style lang="scss" scoped>
$avatar-size: 2.5rem;

.message-container {
  width: 100%;
  display: flex;
  flex-direction: column;

  .recipient-name {
    margin-left: 2.5rem;
    padding: var(--spacing-100) 0 var(--spacing-25) var(--spacing-50);
  }

  .message-content {
    display: flex;
    align-items: flex-end;

    .delete-button {
      flex-shrink: 0;
      border-radius: 100%;
      transition:
        transform 0.2s 0.1s,
        opacity 0.2s 0.1s,
        background-color 0.2s;
      opacity: 0;
      transform: translateX(-50%);
      margin-left: var(--spacing-50);
      margin-right: 0;
    }

    .recipient-avatar {
      visibility: hidden;
      margin-right: var(--spacing-50);
      width: var(--spacing-200);
      flex-shrink: 0;
      height: var(--spacing-200);

      &.show {
        visibility: visible;
      }
    }

    .text {
      border-radius: var(--radius-medium);
      padding: var(--spacing-50) var(--spacing-75);
      width: fit-content;
      transition:
        transform 0.2s 0.1s,
        opacity 0.2s 0.1s,
        background-color 0.2s;
      word-break: break-word;
      margin: 0 0 var(--spacing-25) 0;
      background-color: var(--gray-96);
      white-space: pre-wrap;

      &.active {
        opacity: 0.8;
      }

      &.first {
        border-bottom-left-radius: 0;
      }

      &.last:not(.first) {
        border-top-left-radius: 0;
      }

      &:not(.first, .last) {
        border-bottom-left-radius: 0;
        border-top-left-radius: 0;
      }
    }

    &:hover .delete-button,
    .delete-button:focus {
      transform: initial;
      opacity: 1;
    }
  }

  .message-timestamp {
    display: flex;
    padding-top: var(--spacing-50);
    margin-left: $avatar-size;
  }
}
</style>
